/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import { Select, Typography } from 'antd';
import { FiSearch } from 'react-icons/fi';
import ReCAPTCHA from 'react-google-recaptcha';
import { VerificationReason } from '../../types';

const { Title, Text } = Typography;

const REASON_OPTIONS: VerificationReason[] = [
  'Peace of Mind',
  'Suspicious Document',
  'KYC Requirement',
  'Audit requirement',
];

interface Props {
  documentNo: string;
  verificationReason: string;
  captchaChecked: boolean;
  isLoading: boolean;
  isReady: boolean;
  orgName: string;
  onDocumentNoChange: (val: string) => void;
  onReasonChange: (val: string) => void;
  onCaptchaChange: (checked: boolean) => void;
  onSubmit: () => void;
}

export default function EVerifyDocumentSearchForm({
  documentNo,
  verificationReason,
  isLoading,
  isReady,
  orgName,
  onDocumentNoChange,
  onReasonChange,
  onCaptchaChange,
  onSubmit,
}: Props) {
  const recaptchaSiteKey =
    typeof window !== 'undefined'
      ? (window as any).__RUNTIME_CONFIG__?.NEXT_PUBLIC_RECAPTCHA_SITE_KEY
      : process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '';

  return (
    <div
      style={{
        width: '100%',
        background: '#ffffff',
        border: '2px solid var(--color-primary)',

        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {(['top-left', 'top-right', 'bottom-left', 'bottom-right'] as const).map(
        (corner) => {
          const isTop = corner.includes('top');
          const isLeft = corner.includes('left');
          return (
            <div
              key={corner}
              style={{
                position: 'absolute',
                top: isTop ? 10 : undefined,
                bottom: !isTop ? 10 : undefined,
                left: isLeft ? 10 : undefined,
                right: !isLeft ? 10 : undefined,
                width: 36,
                height: 36,
                borderTop: isTop ? '2px solid var(--color-primary)' : undefined,
                borderBottom: !isTop
                  ? '2px solid var(--color-primary)'
                  : undefined,
                borderLeft: isLeft
                  ? '2px solid var(--color-primary)'
                  : undefined,
                borderRight: !isLeft
                  ? '2px solid var(--color-primary)'
                  : undefined,
                pointerEvents: 'none',
              }}
            />
          );
        },
      )}

      <div
        className="esf-header"
        style={{
          borderBottom: '1px solid var(--color-primary)',
          background: '#f4faf6',
        }}
      >
        <div
          style={{
            textAlign: 'center',
            marginBottom: 18,
            fontSize: 14,
            fontWeight: 700,
            color: '#0f172a',
          }}
        >
          Official Documents Verification
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 24,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: 1, minWidth: 200 }}>
            <Title level={2} className="mb-1! text-[22px]! sm:text-[26px]!">
              Verify a Document
            </Title>
            <Text
              type="secondary"
              className="text-sm"
              style={{ fontStyle: 'italic' }}
            >
              Enter the document number issued by {orgName} and your reason for
              verification to proceed. All fields are required.
            </Text>
          </div>

          <div
            style={{
              width: 110,
              height: 110,
              borderRadius: '50%',
              border: '2px solid var(--color-primary)',
              background: '#f4faf6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              flexShrink: 0,
              gap: 6,
            }}
          >
            <FiSearch size={26} color="var(--color-primary)" />
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: 'var(--color-primary)',
              }}
            >
              Step 1 of 2
            </span>
          </div>
        </div>
      </div>

      <div className="esf-body" style={{ background: '#ffffff' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div className="esf-field-block">
            <div className="esf-field-label">
              Document Number <span style={{ color: '#b42318' }}>*</span>
            </div>
            <input
              type="text"
              value={documentNo}
              onChange={(e) => onDocumentNoChange(e.target.value)}
              placeholder="Enter the document number you wish to verify"
              style={{
                width: '100%',
                border: '1px solid #b8cfc2',
                background: '#f8fcf9',
                padding: '12px 14px',
                fontSize: 14,
                color: '#0f172a',
                outline: 'none',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--color-primary)';
                e.target.style.boxShadow = '0 0 0 3px rgba(0, 99, 0, 0.12)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#b8cfc2';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          <div className="esf-field-block">
            <div className="esf-field-label">
              Reason for Verification{' '}
              <span style={{ color: '#b42318' }}>*</span>
            </div>

            <Select
              value={verificationReason || undefined}
              onChange={(value) => onReasonChange(value)}
              placeholder="Select reason for verifying this document"
              options={REASON_OPTIONS.map((r) => ({
                label: r,
                value: r,
              }))}
              className="esf-ant-select"
              popupClassName="esf-ant-select-dropdown"
              style={{ width: '100%' }}
              size="large"
            />
          </div>

          <div className="esf-field-block" style={{ borderBottom: 'none' }}>
            <div className="esf-field-label">
              Captcha Verification <span style={{ color: '#b42318' }}>*</span>
            </div>

            <div
              style={{
                border: '1px solid #b8cfc2',
                background: '#f8fcf9',
                padding: '12px 14px',
                overflowX: 'auto',
              }}
            >
              {recaptchaSiteKey ? (
                <ReCAPTCHA
                  sitekey={recaptchaSiteKey}
                  onChange={(token) => onCaptchaChange(!!token)}
                  onExpired={() => onCaptchaChange(false)}
                  onErrored={() => onCaptchaChange(false)}
                />
              ) : (
                <div
                  style={{
                    fontSize: 13,
                    color: '#475569',
                  }}
                >
                  ReCAPTCHA site key missing.
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={{ marginTop: 28 }}>
          <button
            onClick={onSubmit}
            disabled={!isReady || isLoading}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              width: '100%',
              padding: '12px 20px',
              fontSize: 13,
              fontWeight: 700,
              color: '#ffffff',
              background:
                !isReady || isLoading ? '#94a3b8' : 'var(--color-primary)',
              border: 'none',
              cursor: !isReady || isLoading ? 'not-allowed' : 'pointer',
              transition: 'background 0.15s ease, transform 0.15s ease',
            }}
            onMouseEnter={(e) => {
              if (!isReady || isLoading) return;
              e.currentTarget.style.background =
                'color-mix(in srgb, var(--color-primary) 85%, black)';
            }}
            onMouseLeave={(e) => {
              if (!isReady || isLoading) return;
              e.currentTarget.style.background = 'var(--color-primary)';
            }}
          >
            {isLoading && (
              <svg
                style={{
                  animation: 'esf-spin 1s linear infinite',
                  width: 14,
                  height: 14,
                }}
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  opacity="0.25"
                />
                <path
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                  opacity="0.75"
                />
              </svg>
            )}
            {isLoading ? 'Verifying Document…' : 'Verify Document →'}
          </button>
        </div>
      </div>

      <div
        style={{
          borderTop: '1px solid var(--color-primary)',
          background: '#f4faf6',
          padding: '10px 36px',
          textAlign: 'center',
        }}
      >
        <Text type="secondary" style={{ fontSize: 11 }}>
          All verification queries are logged for audit and security purposes.
        </Text>
      </div>

      <style>{`
        .esf-header { padding: 24px 36px 20px; }
        .esf-body { padding: 28px 36px 32px; }
        .esf-field-block { padding: 14px 0; border-bottom: 1px solid #e1eee6; }
        .esf-field-label {
          font-size: 11px;
          font-weight: 700;
          color: #64748b;
          margin-bottom: 8px;
        }

        .esf-select-wrap {
          position: relative;
          width: 100%;
        }

        .esf-select {
          width: 100%;
          min-height: 46px;
          border: 1px solid #b8cfc2;
          background: #f8fcf9;
          padding: 12px 46px 12px 14px;
          font-size: 14px;
          color: ${verificationReason ? '#0f172a' : '#6d857a'};
          outline: none;
          font-family: inherit;
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          cursor: pointer;
          box-sizing: border-box;
          line-height: 1.4;
          transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
        }

        .esf-select-icon {
          position: absolute;
          right: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--color-primary);
          font-size: 16px;
          display: inline-flex;
          align-items: center;
          pointer-events: none;
        }

        @keyframes esf-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @media (max-width: 992px) {
          .esf-header { padding: 20px 24px 16px; }
          .esf-body { padding: 20px 24px 28px; }
        }

        @media (max-width: 600px) {
          .esf-header { padding: 18px 16px 14px; }
          .esf-body { padding: 16px 16px 24px; }

          .esf-select {
            min-height: 50px;
            font-size: 16px;
            padding: 13px 46px 13px 14px;
          }
        }
      `}</style>
    </div>
  );
}
